import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { useChosenDepartment } from "../../../../contexts/ChosenDepartment";
import { useSnackbar } from "../../../../contexts/SnackbarContext";
import { backendService } from "../../../../services/backend.service";
import type { Agency } from "../../../../utils/models";
import axiosInstance from "../../../../utils/networkWrapper";

export const useAgencies = () => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const { showSnackbar } = useSnackbar();
	const { chosenDepartment } = useChosenDepartment();

	const {
		data: agencies = [],
		isLoading,
		refetch,
	} = useQuery<Agency[]>({
		queryKey: ["agencies", chosenDepartment.department_id],
		queryFn: async () => {
			const response = await backendService.getAgencies(chosenDepartment.department_id);
			return response.data;
		},
		initialData: [],
	});

	const { data: allAgencies = [] } = useQuery<Agency[]>({
		queryKey: ["agencies"],
		queryFn: async () => {
			const response = await backendService.getAgencies();
			return response.data;
		},
		initialData: [],
	});

	const createAgencyMutation = useMutation({
		mutationFn: async (agencyName: string) => {
			const response = await axiosInstance.post<Agency>(
				"/agency-wages/agencies",
				{ name: agencyName },
				{ params: { department_id: chosenDepartment.department_id } },
			);
			return response.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["agencies", chosenDepartment.department_id],
			});
			showSnackbar(t("agency.created.success"), "success");
		},
		onError: (error: AxiosError<{ detail: string }>) => {
			const errorMessage = error?.response?.data?.detail || t("agency.create.failed");
			showSnackbar(errorMessage, "error");
		},
	});

	return {
		agencies: agencies.map((a) => a.name),
		allAgencies: allAgencies,
		agenciesData: agencies,
		isLoading,
		refetchAgencies: refetch,
		createAgency: createAgencyMutation.mutateAsync,
		isCreatingAgency: createAgencyMutation.isPending,
	};
};
